import { inject, Signal } from '@angular/core';

import {
  injectQuery,
  keepPreviousData,
} from '@tanstack/angular-query-experimental';

import { firstValueFrom, Observable } from 'rxjs';

import {
  ClientAdminApiService,
  UserActionStatsRangeParams,
  UserActionStatsResponse,
} from '../services/client-admin-api.service';

/** The user-action aggregations of `POST /client-admin/user_action_stats_*`, one widget each. */
export type UserActionStatsWidget =
  | 'hourly_active_users'
  | 'top_active_companies'
  | 'top_action_types'
  | 'errors_per_hour';

const TOP_ACTIVE_COMPANIES_LIMIT = 20;
const TOP_ACTION_TYPES_LIMIT = 20;

/**
 * One user-action statistic over a day range, keyed on the widget and the
 * range; `null` range = widget not shown, nothing fetched. The previous series
 * stays on screen while a new range loads. A dashboard Refresh invalidates
 * `['client_admin', 'user_action_stats']`.
 */
export function injectUserActionStatsQuery(
  widget: UserActionStatsWidget,
  range: Signal<UserActionStatsRangeParams | null>,
) {
  const api = inject(ClientAdminApiService);
  const fetchers: Record<
    UserActionStatsWidget,
    (range: UserActionStatsRangeParams) => Observable<UserActionStatsResponse>
  > = {
    hourly_active_users: (params) =>
      api.getUserActionStatsDailyActiveUsers(params),
    top_active_companies: (params) =>
      api.getUserActionStatsTopActiveCompanies({
        ...params,
        limit: TOP_ACTIVE_COMPANIES_LIMIT,
      }),
    top_action_types: (params) =>
      api.getUserActionStatsTopActionTypes({
        ...params,
        limit: TOP_ACTION_TYPES_LIMIT,
      }),
    errors_per_hour: (params) => api.getUserActionStatsErrorsPerDay(params),
  };

  return injectQuery(() => ({
    queryKey: ['client_admin', 'user_action_stats', widget, range()],
    queryFn: () => firstValueFrom(fetchers[widget](range()!)),
    enabled: !!range(),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    retry: 1,
  }));
}
